import React, { useState, useEffect } from 'react';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { toast } from 'react-toastify';
import { User, Save, X, Loader2, AlertCircle, Mail, Tag } from 'lucide-react';
import classNames from 'classnames';

const InputField = ({ label, value, onChange, disabled, type = "text", helperText, icon: Icon }) => (
  <div className="space-y-1.5 w-full">
    <label className="text-xs font-medium text-gray-400 uppercase tracking-wide ml-1">
      {label}
    </label>
    <div className="relative group">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-500 group-focus-within:text-primary transition-colors duration-300" />
        </div>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={classNames(
          "block w-full bg-[#1a1a1a] border border-[#333] rounded-lg text-white placeholder-gray-600 transition-all duration-300 sm:text-sm focus:outline-none",
          {
            "pl-10": Icon,
            "pl-3": !Icon,
            "py-2.5": true,
            "opacity-60 cursor-not-allowed bg-gray-900": disabled,
            "focus:border-primary/50 focus:ring-1 focus:ring-primary/50": !disabled
          }
        )}
      />
    </div>
    {helperText && (
      <p className="text-xs text-gray-500 ml-1">{helperText}</p>
    )}
  </div>
);

const UserProfile = ({ open, onClose }) => {
  const { profile, loading, error, getProfile, updateProfile } = useUserProfile();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open || open === undefined) {
      loadProfile();
    }
  }, [open]);

  const loadProfile = async () => {
    try {
      const userProfile = await getProfile();
      setFormData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        email: userProfile.email || ''
      });
    } catch (error) {
      // Error already handled in context
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateProfile(formData);
      toast.success('Profil güncellendi');
      if (onClose) {
        onClose();
      }
    } catch (error) {
      // Error already handled in context
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const fieldsContent = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <InputField
        label="Kullanıcı Adı"
        value={profile?.username || ''}
        disabled
        helperText="Kullanıcı adı değiştirilemez"
        icon={User}
      />
      <InputField
        label="Rol"
        value={profile?.role || ''}
        disabled
        helperText="Rol değiştirilemez"
        icon={Tag}
      />
      <InputField
        label="Ad"
        value={formData.firstName}
        onChange={handleChange('firstName')}
      />
      <InputField
        label="Soyad"
        value={formData.lastName}
        onChange={handleChange('lastName')}
      />
      <div className="md:col-span-2">
        <InputField
          label="E-posta"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          icon={Mail}
        />
      </div>
    </div>
  );

  const LoadingState = () => (
    <div className="flex justify-center p-8">
      <Loader2 className="w-8 h-8 text-primary animate-spin" />
    </div>
  );

  const ErrorState = () => (
    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3 text-red-400">
      <AlertCircle className="w-5 h-5" />
      <span>{error}</span>
    </div>
  );

  const SaveButton = () => (
    <button
      onClick={handleSave}
      disabled={saving || loading}
      className="flex items-center justify-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
      {saving ? 'Kaydediliyor...' : 'Kaydet'}
    </button>
  );

  // Dialog Mode
  if (open !== undefined) {
    if (!open) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-2xl bg-[#121212] border border-white/10 rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/5">
            <div className="flex items-center gap-3 text-white">
              <User className="w-6 h-6 text-primary" />
              <h2 className="text-xl font-bold">Profil Yönetimi</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto custom-scrollbar">
            {loading ? <LoadingState /> : error ? <ErrorState /> : fieldsContent}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-white/5 bg-[#1a1a1a]/50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors font-medium border border-transparent hover:border-gray-600 rounded-lg"
            >
              İptal
            </button>
            <SaveButton />
          </div>
        </div>
      </div>
    );
  }

  // Page Mode
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <div className="p-2 bg-primary/10 rounded-lg">
          <User className="w-6 h-6 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-white">Profil Yönetimi</h1>
      </div>

      <div className="bg-[#121212] border border-white/5 rounded-xl p-8 shadow-xl">
        {loading ? <LoadingState /> : error ? <ErrorState /> : (
          <>
            {fieldsContent}
            <div className="flex justify-end mt-8 pt-6 border-t border-white/5">
              <SaveButton />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserProfile;

